/**
 * Deep Link Handler Hook
 *
 * Handles deep links from external sources (e.g., Stripe Checkout redirects, AppsFlyer OneLink).
 * Listens for `appUrlOpen` events from Capacitor's App plugin and routes accordingly.
 *
 * Deep link sources:
 * 1. AppsFlyer OneLink (UDL) - For marketing/attribution links
 * 2. Capacitor App plugin - For direct paire:// and universal links
 *
 * Supported deep link routes:
 * - subscription/success - Stripe checkout success (converges through `useInvalidateSubscription()` FIRST, then navigates)
 * - subscription/canceled - Stripe checkout canceled
 * - lists/collaborative/:urlKey - Open collaborative list
 * - profile/:userId - Open user profile
 * - restaurants/:restaurantId - Open restaurant details
 *
 * AppsFlyer OneLink mapping:
 * - deep_link_value: Route type (e.g., 'collab_list', 'restaurant', 'profile')
 * - deep_link_sub1: Primary ID parameter
 * - deep_link_sub2: Secondary parameter (optional)
 */

import { useEffect, useCallback, useRef } from 'react';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from '@tanstack/react-router';
import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { useInvalidateSubscription } from '@/api/queries/useSubscriptionQueries';
import { BILLING_CANCELED_TOAST, BILLING_SUCCESS_TOAST, useBillingReturnToast } from '@/hooks/useBillingReturnToast';
import { appsFlyerService, DeepLinkCallback } from '@/lib/appsflyer';

/**
 * Parse a deep link URL and extract the route and params.
 */
function parseDeepLink(url: string): { route: string; params: Record<string, string> } | null {
  try {
    // Handle both paire:// scheme and https://app.paire.io URLs
    let path: string;

    if (url.startsWith('paire://')) {
      // Custom scheme: paire://subscription/success
      path = url.replace('paire://', '');
    } else if (url.includes('app.paire.io')) {
      // Universal link: https://app.paire.io/subscription/success
      const urlObj = new URL(url);
      path = urlObj.pathname.replace(/^\//, '');

      // Also check for query params
      const queryParams: Record<string, string> = {};
      urlObj.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });

      if (Object.keys(queryParams).length > 0) {
        return { route: path, params: queryParams };
      }
    } else {
      return null;
    }

    // Parse route segments
    const segments = path.split('/').filter(Boolean);

    if (segments.length === 0) {
      return null;
    }

    return {
      route: segments.join('/'),
      params: {},
    };
  } catch {
    return null;
  }
}

/** Records a failed subscription-success navigation without its message (it can echo a URL). */
function reportNavigationFailure(error: unknown): void {
  Sentry.addBreadcrumb({
    category: 'deep-link',
    level: 'warning',
    message: 'subscription success navigation failed',
    data: { errorName: error instanceof Error ? error.name : typeof error },
  });
}

/**
 * Hook to handle deep links from external sources.
 * Should be called once at the app root level.
 *
 * Initializes AppsFlyer SDK and registers UDL listener for OneLink deep links.
 * Also maintains Capacitor App listener as fallback for non-AppsFlyer links.
 */
export function useDeepLinkHandler(): void {
  const navigate = useNavigate();
  const toast = useToast();
  const showReturnToast = useBillingReturnToast();
  const refreshBilling = useInvalidateSubscription();
  const appsFlyerInitialized = useRef(false);

  /**
   * The subscription-success return, shared by the AppsFlyer UDL callback and
   * the raw `appUrlOpen` handler. Convergence comes FIRST and never depends on
   * navigation (D51f Phase 5, OQ-41): `refreshBilling` is the same point the
   * web return and the foreground listener use. Then the one return toast (the
   * id `SubscriptionSettings` also uses, so the two paths cannot stack), then
   * the navigation, guarded so its failure can neither throw out of the
   * handler nor become an unhandled rejection.
   */
  const handleSubscriptionSuccess = useCallback(() => {
    void refreshBilling();
    showReturnToast({
      ...BILLING_SUCCESS_TOAST,
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
    try {
      // The `navigate` function `useNavigate()` returned throws synchronously when there is no router above this
      // hook (calling it is what throws, not the hook), and rejects if the navigation itself fails: both are
      // absorbed. Only the error's class name is recorded, never its message.
      Promise.resolve(navigate({ to: '/settings/subscription', search: { success: 'true' } })).catch(reportNavigationFailure);
    } catch (error) {
      reportNavigationFailure(error);
    }
  }, [navigate, refreshBilling, showReturnToast]);

  /**
   * Handle AppsFlyer OneLink deep links via UDL.
   * Maps OneLink parameters to app routes.
   */
  const handleAppsFlyerDeepLink: DeepLinkCallback = useCallback(
    ({ deepLinkValue, deepLinkSub1, deepLinkSub2, isDeferred }) => {
      console.log('[DeepLink] AppsFlyer UDL:', { deepLinkValue, deepLinkSub1, deepLinkSub2, isDeferred });

      // Map OneLink deep_link_value to app routes
      switch (deepLinkValue) {
        case 'collab_list':
        case 'collaborative_list':
          // Collaborative list: deep_link_sub1 = urlKey
          if (deepLinkSub1) {
            navigate({ to: '/lists/collaborative/$urlKey', params: { urlKey: deepLinkSub1 } });
          }
          break;

        case 'restaurant':
          // Restaurant: deep_link_sub1 = restaurantId
          if (deepLinkSub1) {
            const restaurantId = Number(deepLinkSub1);
            if (!isNaN(restaurantId)) {
              navigate({ to: '/restaurants/$restaurantId', params: { restaurantId } });
            }
          }
          break;

        case 'profile':
          // User profile: deep_link_sub1 = userId
          if (deepLinkSub1) {
            const userId = Number(deepLinkSub1);
            if (!isNaN(userId)) {
              navigate({ to: '/profile/$userId', params: { userId } });
            }
          }
          break;

        case 'subscription_success':
          // Subscription success (from external purchase flow)
          handleSubscriptionSuccess();
          break;

        case 'subscription_canceled':
          toast({
            ...BILLING_CANCELED_TOAST,
            status: 'info',
            duration: 4000,
            isClosable: true,
          });
          navigate({ to: '/settings/subscription' });
          break;

        default:
          // Try to use deep_link_value as a direct route
          if (deepLinkValue) {
            console.log('[DeepLink] Navigating to deep_link_value:', deepLinkValue);
            navigate({ to: `/${deepLinkValue}` as never });
          }
          break;
      }
    },
    [navigate, toast, handleSubscriptionSuccess]
  );

  /**
   * Handle traditional deep links (paire:// scheme and universal links).
   * Fallback for non-AppsFlyer links.
   */
  const handleDeepLink = useCallback(
    async (url: string) => {
      const parsed = parseDeepLink(url);

      if (!parsed) {
        console.warn('[DeepLink] Could not parse URL:', url);
        return;
      }

      const { route, params } = parsed;

      // Handle subscription-related deep links
      if (route === 'subscription/success' || params.success === 'true') {
        handleSubscriptionSuccess();
        return;
      }

      if (route === 'subscription/canceled' || params.canceled === 'true') {
        toast({
          ...BILLING_CANCELED_TOAST,
          status: 'info',
          duration: 4000,
          isClosable: true,
        });

        navigate({ to: '/settings/subscription' });
        return;
      }

      // Handle collaborative list deep links
      if (route.startsWith('lists/collaborative/')) {
        const urlKey = route.split('/')[2];
        if (urlKey) {
          navigate({ to: '/lists/collaborative/$urlKey', params: { urlKey } });
          return;
        }
      }

      // Handle profile deep links
      if (route.startsWith('profile/')) {
        const userIdStr = route.split('/')[1];
        const userId = userIdStr ? Number(userIdStr) : NaN;
        if (!isNaN(userId)) {
          navigate({ to: '/profile/$userId', params: { userId } });
          return;
        }
      }

      // Handle restaurant deep links (route is /restaurants/, plural)
      if (route.startsWith('restaurants/') || route.startsWith('restaurant/')) {
        const restaurantIdStr = route.split('/')[1];
        const restaurantId = restaurantIdStr ? Number(restaurantIdStr) : NaN;
        if (!isNaN(restaurantId)) {
          navigate({ to: '/restaurants/$restaurantId', params: { restaurantId } });
          return;
        }
      }

      // Fallback: try to navigate to the route as-is
      console.log('[DeepLink] Navigating to:', `/${route}`);
      navigate({ to: `/${route}` as never });
    },
    [navigate, toast, handleSubscriptionSuccess]
  );

  useEffect(() => {
    // Only set up listener on native platforms
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    // Initialize AppsFlyer SDK with UDL callback for OneLink deep links
    // This should happen once, early in the app lifecycle
    if (!appsFlyerInitialized.current) {
      appsFlyerInitialized.current = true;
      appsFlyerService.init(
        handleAppsFlyerDeepLink, // UDL callback for OneLink
        (data) => {
          // Conversion callback - log attribution data for debugging
          console.log('[DeepLink] AppsFlyer attribution:', data);
          // Handle deferred deep links from conversion data (fallback)
          if (data.isFirstLaunch && data.deepLink) {
            handleDeepLink(data.deepLink);
          }
        }
      );
    }

    // Listen for deep links when app is running (fallback for non-AppsFlyer links)
    const listener = App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      console.log('[DeepLink] Received URL:', event.url);
      handleDeepLink(event.url);
    });

    // Check if app was opened via deep link (cold start)
    App.getLaunchUrl().then((launchUrl) => {
      if (launchUrl?.url) {
        console.log('[DeepLink] Launched with URL:', launchUrl.url);
        handleDeepLink(launchUrl.url);
      }
    });

    return () => {
      listener.then((l) => l.remove());
    };
  }, [handleDeepLink, handleAppsFlyerDeepLink]);
}

export default useDeepLinkHandler;
